/**
 * Uma linha das Configurações (settingsModel.ts): o rótulo e a descrição à esquerda e o controle à
 * direita (numa tela estreita, o segmentado, a lista e o deslizante descem para baixo do rótulo). As
 * ações que pedem confirmação trocam a linha pela pergunta, com Voltar e o botão de seguir.
 */
import { useEffect, useState, type ReactNode } from 'react';
import { downloadErrorLog, errorLogSize } from '../errorLog.js';
import { LANGUAGES, useLanguage, useT } from '../i18n/index.js';
import { askSystemNotices, playSound } from '../notify.js';
import { DEFAULT_SETTINGS } from '../settings.js';
import { useApp } from '../store.js';
import { Button, ComboBox, Segmented, Slider, Switch } from '../ui/controls.js';
import { toast } from '../ui/Toaster.js';
import { TermsDialog } from '../states/TermsDialog.js';
import { DeleteAccountConfirm, PasswordDialog } from './AccountParts.js';
import { DeviceSelect, playOnSpeaker, supportsSpeakerChoice } from './AvSection.js';
import { NOTICES_EVENT } from './NoticesBanner.js';
import { optionValue, parseOption, type ActionId, type Row, type SectionId } from './settingsModel.js';
import { useSavePrefs } from './useSavePrefs.js';

export type OpenSection = (id: SectionId, row?: string | null) => void;

/** O esqueleto de toda linha: `data-pref-row` é por onde a busca acha a linha para destacar. */
function RowFrame({ row, flash, stack, label, desc, children }: { row: Row; flash: boolean; stack?: boolean; label: ReactNode; desc?: ReactNode; children?: ReactNode }) {
  return (
    <div className={`rd-prefs__row${stack ? ' rd-prefs__row--stack' : ''}${flash ? ' is-flash' : ''}`} data-pref-row={row.id}>
      <div className="rd-prefs__rowText">
        {label}
        {desc}
      </div>
      {children !== undefined ? <div className="rd-prefs__control">{children}</div> : null}
    </div>
  );
}

/** A pergunta de confirmação no lugar da linha. */
function Confirm({ row, flash, question, desc, yes, danger, onYes, onBack }: { row: Row; flash: boolean; question: string; desc?: string; yes: string; danger?: boolean; onYes: () => void; onBack: () => void }) {
  const t = useT();
  return (
    <RowFrame row={row} flash={flash} label={<span className="rd-prefs__label" role="alert">{question}</span>} desc={desc ? <p className="rd-prefs__desc">{desc}</p> : undefined}>
      <Button variant="secondary" onClick={onBack}>
        {t('prefs.back')}
      </Button>
      <Button variant="action" className={danger ? 'rd-btn--danger' : 'rd-prefs__warnBtn'} autoFocus onClick={onYes}>
        {yes}
      </Button>
    </RowFrame>
  );
}

export function SettingRow({ row, flash, onOpen }: { row: Row; flash: boolean; onOpen: OpenSection }) {
  const t = useT();
  const settings = useApp((s) => s.settings);
  const me = useApp((s) => s.user);
  const save = useSavePrefs();
  const language = useLanguage((s) => s.language);
  const setLanguage = useLanguage((s) => s.setLanguage);
  const [confirming, setConfirming] = useState(false);
  const [dialog, setDialog] = useState(false);
  const [logCount, setLogCount] = useState(errorLogSize);

  const id = `pref-${row.id}`;
  const labelId = `${id}-label`;
  const descId = `${id}-desc`;
  const descKey = row.kind === 'select' ? (row.descByValue?.[optionValue(settings, row.setting)] ?? row.desc) : row.desc;
  const descText = descKey ? t(descKey) : '';
  const description = (extra = '') =>
    descText || extra ? (
      <p className="rd-prefs__desc" id={descId}>
        {[descText, extra].filter(Boolean).join(' ')}
      </p>
    ) : undefined;
  const label = (text: string, forControl = true) =>
    forControl ? (
      <label htmlFor={id} id={labelId} className="rd-prefs__label">
        {text}
      </label>
    ) : (
      <span id={labelId} className="rd-prefs__label">
        {text}
      </span>
    );
  const describedBy = descText ? { 'aria-describedby': descId } : {};

  switch (row.kind) {
    case 'switch': {
      const value = settings[row.setting] === true;
      return (
        <RowFrame row={row} flash={flash} label={label(t(row.label))} desc={description()}>
          <Switch id={id} checked={row.invert ? !value : value} onChange={(on) => save({ [row.setting]: row.invert ? !on : on })} {...describedBy} />
        </RowFrame>
      );
    }
    case 'sound': {
      const value = settings[row.setting] === true;
      const sound = row.sound;
      return (
        <RowFrame row={row} flash={flash} label={label(t(row.label))} desc={description()}>
          {sound ? (
            <Button variant="secondary" icon="play" className="rd-prefs__listen" aria-label={t('prefs.notify.listen', { name: t(row.label) })} onClick={() => playSound(sound, settings.masterVolume)}>
              {t('config.notify.test')}
            </Button>
          ) : null}
          <Switch id={id} checked={value} onChange={(on) => save({ [row.setting]: on })} {...describedBy} />
        </RowFrame>
      );
    }
    case 'segmented':
      return (
        <RowFrame row={row} flash={flash} stack label={label(t(row.label), false)} desc={description()}>
          <Segmented
            aria-labelledby={labelId}
            {...describedBy}
            value={optionValue(settings, row.setting)}
            options={row.options.map((o) => ({ value: o.value, label: t(o.label) }))}
            onChange={(value) => {
              save({ [row.setting]: parseOption(row.setting, value) });
              // pedir aviso de mensagem é a hora de pedir a permissão do sistema
              if (row.setting === 'messageNotice' && value !== 'never') void askSystemNotices().then(() => window.dispatchEvent(new Event(NOTICES_EVENT)));
            }}
          />
        </RowFrame>
      );
    case 'select':
      return (
        <RowFrame row={row} flash={flash} stack label={label(t(row.label))} desc={description()}>
          <ComboBox id={id} {...describedBy} value={optionValue(settings, row.setting)} options={row.options.map((o) => ({ value: o.value, label: t(o.label) }))} onChange={(value) => save({ [row.setting]: parseOption(row.setting, value) })} />
        </RowFrame>
      );
    case 'slider': {
      const percent = row.unit === 'percent';
      const raw = settings[row.setting];
      return (
        <RowFrame row={row} flash={flash} stack label={label(t(row.label))} desc={description()}>
          <Slider
            id={id}
            {...describedBy}
            value={percent ? Math.round(raw * 100) : raw}
            min={percent ? row.min * 100 : row.min}
            max={percent ? row.max * 100 : row.max}
            step={percent ? row.step * 100 : row.step}
            format={(v) => (percent ? `${v}%` : `${v} px`)}
            onChange={(v) => save({ [row.setting]: percent ? v / 100 : v })}
          />
        </RowFrame>
      );
    }
    case 'language':
      return (
        <RowFrame row={row} flash={flash} stack label={label(t(row.label))} desc={description()}>
          <ComboBox
            id={id}
            value={language}
            options={LANGUAGES.map((l) => ({ value: l.id, label: l.label }))}
            onChange={(value) => {
              setLanguage(value);
              toast(t('prefs.saved'), 'success');
            }}
          />
        </RowFrame>
      );
    case 'device': {
      if (row.device === 'audiooutput' && !supportsSpeakerChoice()) return null;
      const value = settings[row.setting];
      return (
        <RowFrame row={row} flash={flash} stack label={label(t(row.label))} desc={description()}>
          <DeviceSelect id={id} kind={row.device} value={value} fallbackLabel={t(row.label)} {...(descText ? { describedBy: descId } : {})} onChange={(v) => save({ [row.setting]: v })} />
          {row.device === 'audiooutput' ? (
            <Button variant="secondary" icon="play" className="rd-prefs__listen" aria-label={t('prefs.av.speaker.test')} onClick={() => void playOnSpeaker(value, settings.masterVolume).catch(() => toast(t('prefs.av.speaker.error'), 'error'))}>
              {t('config.notify.test')}
            </Button>
          ) : null}
        </RowFrame>
      );
    }
    case 'info': {
      const value =
        row.info === 'accountName'
          ? (me?.displayName ?? '')
          : me
            ? new Date(me.createdAt).toLocaleDateString(language, { day: '2-digit', month: 'long', year: 'numeric' })
            : '';
      return (
        <RowFrame row={row} flash={flash} label={label(t(row.label), false)}>
          <strong className="rd-prefs__info" aria-labelledby={labelId}>
            {value}
          </strong>
        </RowFrame>
      );
    }
    case 'privacy':
      return (
        <RowFrame row={row} flash={flash} label={label(t(row.label))} desc={description()}>
          <ImsPrivacySwitch id={id} {...describedBy} />
        </RowFrame>
      );
    case 'action':
      return <ActionRow row={row} action={row.action} flash={flash} onOpen={onOpen} confirming={confirming} setConfirming={setConfirming} dialog={dialog} setDialog={setDialog} logCount={logCount} setLogCount={setLogCount} label={label(t(row.label), false)} description={description} />;
    case 'block':
      return null;
  }
}

/**
 * "Receber mensagens de quem não é amigo" (docs/spec/friends.md): lê da conta ao abrir e grava na
 * hora, no servidor. Até ler (ou sem conexão), a chave fica parada.
 */
function ImsPrivacySwitch({ id, ...describedBy }: { id: string; 'aria-describedby'?: string }) {
  const t = useT();
  const privacy = useApp((s) => s.privacy);
  const loadPrivacy = useApp((s) => s.loadPrivacy);
  const setImsPrivacy = useApp((s) => s.setImsPrivacy);
  useEffect(() => {
    void loadPrivacy();
  }, [loadPrivacy]);
  return (
    <Switch
      id={id}
      checked={privacy?.ims !== 'friends'}
      disabled={!privacy}
      onChange={async (on) => {
        if (await setImsPrivacy(on ? 'everyone' : 'friends')) toast(t('prefs.saved'), 'success');
      }}
      {...describedBy}
    />
  );
}

/** As linhas de ação: cada uma com o seu botão e, quando precisa, a confirmação na linha. */
function ActionRow({
  row,
  action,
  flash,
  onOpen,
  confirming,
  setConfirming,
  dialog,
  setDialog,
  logCount,
  setLogCount,
  label,
  description,
}: {
  row: Row;
  action: ActionId;
  flash: boolean;
  onOpen: OpenSection;
  confirming: boolean;
  setConfirming: (v: boolean) => void;
  dialog: boolean;
  setDialog: (v: boolean) => void;
  logCount: number;
  setLogCount: (n: number) => void;
  label: ReactNode;
  description: (extra?: string) => ReactNode;
}) {
  const t = useT();
  const update = useApp((s) => s.updateSettings);

  switch (action) {
    case 'changePassword':
      return (
        <>
          <RowFrame row={row} flash={flash} label={label} desc={description()}>
            <Button variant="secondary" onClick={() => setDialog(true)}>
              {t('config.account.changePassword')}
            </Button>
          </RowFrame>
          {dialog ? <PasswordDialog onClose={() => setDialog(false)} /> : null}
        </>
      );
    case 'deleteAccount':
      return confirming ? (
        <div className={`rd-prefs__row rd-prefs__row--stack${flash ? ' is-flash' : ''}`} data-pref-row={row.id}>
          <DeleteAccountConfirm onBack={() => setConfirming(false)} />
        </div>
      ) : (
        <RowFrame row={row} flash={flash} label={label} desc={description()}>
          <Button variant="secondary" className="rd-prefs__dangerBtn" onClick={() => setConfirming(true)}>
            {t('config.account.delete')}
          </Button>
        </RowFrame>
      );
    case 'viewTerms':
    case 'viewPrivacy':
      return (
        <>
          <RowFrame row={row} flash={flash} label={label} desc={description()}>
            <Button variant="secondary" onClick={() => setDialog(true)}>
              {t('prefs.account.read')}
            </Button>
          </RowFrame>
          {dialog ? <TermsDialog initial={action === 'viewTerms' ? 'terms' : 'privacy'} onClose={() => setDialog(false)} /> : null}
        </>
      );
    case 'downloadLog':
      return (
        <RowFrame row={row} flash={flash} label={label} desc={description(t('config.utils.logCount', { n: logCount }))}>
          <Button
            variant="secondary"
            icon="download"
            onClick={() => {
              downloadErrorLog();
              setLogCount(errorLogSize());
            }}
          >
            {t('prefs.support.log.download')}
          </Button>
        </RowFrame>
      );
    case 'goNoLighting':
      return (
        <RowFrame row={row} flash={flash} label={label} desc={description()}>
          <Button variant="secondary" onClick={() => onOpen('dice', 'dice3DLighting')}>
            {t('prefs.support.dice.go')}
          </Button>
        </RowFrame>
      );
    case 'resetAll':
      return confirming ? (
        <Confirm
          row={row}
          flash={flash}
          question={t('prefs.support.resetAll.confirm')}
          desc={t('prefs.support.resetAll.desc')}
          yes={t('prefs.support.resetAll.yes')}
          onBack={() => setConfirming(false)}
          onYes={() => {
            update({ ...DEFAULT_SETTINGS });
            setConfirming(false);
            toast(t('prefs.support.resetAll.done'), 'success');
          }}
        />
      ) : (
        <RowFrame row={row} flash={flash} label={label} desc={description()}>
          <Button variant="secondary" className="rd-prefs__warnOutline" onClick={() => setConfirming(true)}>
            {t('prefs.support.resetAll.button')}
          </Button>
        </RowFrame>
      );
    case 'forgetLayouts':
      return confirming ? (
        <Confirm
          row={row}
          flash={flash}
          question={t('prefs.support.layouts.confirm')}
          desc={t('config.advanced.resetLayoutsConfirm')}
          yes={t('prefs.support.layouts.yes')}
          onBack={() => setConfirming(false)}
          onYes={() => {
            for (const key of Object.keys(localStorage)) if (key.startsWith('rpgdesk.roomLayout.')) localStorage.removeItem(key);
            setConfirming(false);
            toast(t('prefs.support.layouts.done'), 'success');
          }}
        />
      ) : (
        <RowFrame row={row} flash={flash} label={label} desc={description()}>
          <Button variant="secondary" className="rd-prefs__warnOutline" onClick={() => setConfirming(true)}>
            {t('prefs.support.layouts.button')}
          </Button>
        </RowFrame>
      );
  }
}
